import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, Clock } from "lucide-react";
import { CLINIC, getOpenStatus } from "@/data/constants";

export const Header = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const location = useLocation();
  const status = getOpenStatus();

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/#doctor", label: "Doctor" },
    { to: "/#services", label: "Services" },
    { to: "/#clinic", label: "Our Clinic" },
    { to: "/#reviews", label: "Reviews" },
    { to: "/contact", label: "Contact" },
  ];

  const isActive = (to) => to === location.pathname;

  return (
    <header data-testid="site-header" className="sticky top-0 z-50">
      {/* Top info bar */}
      <div className="bg-sage-600 text-sage-50 text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-9 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 font-600">
            <Clock size={13} /> Mon–Sat 9AM–9:30PM · Sun 1PM–5PM
          </span>
          <a href={`tel:${CLINIC.phone}`} className="inline-flex items-center gap-1.5 font-600 hover:text-white" data-testid="topbar-phone">
            <Phone size={13} /> {CLINIC.phoneDisplay}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-sage-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-center justify-between h-20 gap-4">
            <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
              <div className="w-11 h-11 rounded-full bg-sage-500 flex items-center justify-center">
                <span className="font-heading text-white text-2xl leading-none">K</span>
              </div>
              <div className="leading-tight">
                <div className="font-heading text-xl sm:text-2xl font-600 text-sage-700 tracking-tight">
                  Kalpataru
                </div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-sage-500 font-600">
                  Dental Care
                </div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-7">
              {navLinks.map((l) => (
                <a
                  key={l.label}
                  href={l.to === "/contact" ? "/contact" : l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={`text-sm font-600 transition-colors hover:text-sage-600 ${
                    isActive(l.to) ? "text-sage-700" : "text-sage-500"
                  }`}
                >
                  {l.label}
                </a>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <span
                data-testid="header-status-badge"
                className={`hidden sm:inline-flex items-center gap-1.5 text-xs font-600 px-3 py-1.5 rounded-full ${
                  status.open ? "bg-sage-100 text-sage-700" : "bg-sage-100 text-sage-500"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${status.open ? "bg-emerald-500 animate-pulse" : "bg-sage-400"}`} />
                {status.label}
              </span>

              <a
                href={`tel:${CLINIC.phone}`}
                data-testid="emergency-call-btn"
                className="inline-flex items-center gap-2 bg-danger hover:bg-danger-dark text-white font-700 text-sm px-4 sm:px-5 py-2.5 rounded-full shadow-sm transition-colors hover:-translate-y-0.5 transition-transform duration-200"
              >
                <Phone size={16} strokeWidth={2.5} />
                <span className="hidden sm:inline">Call for Emergency</span>
                <span className="inline sm:hidden">Emergency</span>
              </a>

              <button
                data-testid="mobile-menu-toggle"
                className="lg:hidden p-2 text-sage-700"
                onClick={() => setOpenMenu(!openMenu)}
              >
                {openMenu ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {openMenu && (
          <div className="lg:hidden border-t border-sage-200 bg-white px-5 py-4 space-y-2">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.to === "/contact" ? "/contact" : l.to}
                className="block py-2 text-sage-600 font-600"
                onClick={() => setOpenMenu(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};
